/**
 * WCAG contrast helper for the theme editor's live "low contrast" warning (feature 0019 finding #16).
 *
 * The merchant OWNS their contrast (Shrine keeps `solidButtonLabel` editable, never auto-derived), so this
 * is a non-blocking advisory only: the panel computes the ratio of the solid-button label against Accent 1
 * (the accent-band text/bg pair) and warns when it drops below WCAG AA. Nothing here changes the value.
 */
import { hexToRgb } from '../inspector/controls/colorUtils';

/** WCAG AA minimum contrast ratio for normal-size text. */
export const WCAG_AA_NORMAL = 4.5;

/** sRGB channel → linear-light (WCAG relative-luminance transfer function). */
function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}

/**
 * The WCAG contrast ratio (1–21) between two hex colors, or `null` if either can't be parsed (an
 * unparseable color suppresses the warning rather than showing a false one).
 */
export function contrastRatio(a: string, b: string): number | null {
  const rgbA = hexToRgb(a);
  const rgbB = hexToRgb(b);
  if (!rgbA || !rgbB) return null;
  const lumA = relativeLuminance(rgbA);
  const lumB = relativeLuminance(rgbB);
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** `{ ratio, passesAA }`, or `null` when a color is unparseable. */
export function contrastVerdict(
  foreground: string,
  background: string,
): { ratio: number; passesAA: boolean } | null {
  const ratio = contrastRatio(foreground, background);
  if (ratio === null) return null;
  return { ratio, passesAA: ratio >= WCAG_AA_NORMAL };
}
